import { Refresh } from '@mui/icons-material';
import { Alert, Box, Button } from '@mui/material';
import { isAxiosError } from 'axios';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import {
  Show,
  Tab,
  TabbedShowLayout,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useTranslate,
} from 'react-admin';

import { EditTabButton } from 'components/TabButtons/ToTabButtons';
import { usePermission } from 'providers/auth/permissionProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import SSHTunnelControl from '../vpn/components/SSHTunnelControl';
import { UnitCommandsTab } from './commands';
import { AppsTab, ConfigTab, DevTab, DiagnosticsTab, InfoTab } from './components/UnitShowTabs';
import { ActivityLogTab } from './components/UnitShowTabs/ActivityLogTab';
import { MobileNetwork } from './components/UnitShowTabs/MobileNetwork';
import { ReportsTab } from './components/UnitShowTabs/ReportsTab';
import { SocketsTab } from './components/UnitShowTabs/SocketsTab';
import ResetUnitKeyButton from './components/ResetUnitKeyButton';

const UnitActions = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const record = useRecordContext();
  const refresh = useRefresh();
  const translate = useTranslate();
  const { hasPermission } = usePermission();

  const id = get(record, 'id');
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState<string>('');

  const handleAction = async (action: string, errorMessage: string, successMessage: string) => {
    setLoading(true);
    setAction(action);
    try {
      await dataProvider.post(`units/${id}/${action}`);
      refresh();
    } catch (error) {
      if (isAxiosError(error) && error.response) {
        switch (error.response.status) {
          case 404:
            notify('custom.notification.error.not_found', {
              messageArgs: { item: translate(errorMessage) },
              type: 'error',
            });
            break;
          case 200:
            notify('custom.notification.success', {
              messageArgs: { action: translate(successMessage) },
              type: 'success',
            });
            break;
          default:
            notify('ra.page.error', { type: 'error' });
        }
      }
    } finally {
      setLoading(false);
      setAction('');
    }
  };

  const refreshConfig = async () => {
    await handleAction('refresh-config', 'resources.units.show.links.config', 'ra.action.refresh');
  };

  const fixMissingSimCard = async () => {
    await handleAction('fix-missing-sim', 'resources.units.show.links.sim', 'ra.action.refresh');
  };

  interface UnitLink {
    icon: React.ReactNode;
    linkName: string;
    label: string;
  }

  const links: UnitLink[] = [];

  const canManageDeviceKeys = hasPermission('manage_device_keys');
  const canResetKey = canManageDeviceKeys && record?.ssh_key_state === 'valid';
  const disabledReason = !canResetKey
    ? !canManageDeviceKeys
      ? 'You do not have permission to manage device keys'
      : record?.ssh_key_state === 'expired'
        ? 'Key already expired'
        : 'No valid key'
    : undefined;

  return (
    <>
      <TopToolbar>
        {/* Status bar: sits on the left of the toolbar, the action buttons stay on the right. */}
        {record && (
          <Box sx={{ mr: 'auto', minWidth: 0, display: 'flex', alignItems: 'center' }}>
            <SSHTunnelControl />
          </Box>
        )}
        {record && canManageDeviceKeys && (
          <ResetUnitKeyButton canResetKey={canResetKey} disabledReason={disabledReason} />
        )}
        {record &&
          links.map((link, index) => (
            <Button
              key={`link-${index}`}
              size="small"
              startIcon={link.icon}
              href={record[link.linkName]}
              disabled={!record[link.linkName] || record[link.linkName] === 'about:blank'}
              target="_blank"
            >
              {translate(link.label)}
            </Button>
          ))}

        <Button
          loading={loading && action === 'refresh-config'}
          loadingPosition="start"
          size="small"
          startIcon={<Refresh />}
          onClick={refreshConfig}
          disabled={(loading && action === 'refresh-config') || !hasPermission('dev_options')}
          sx={{
            'backgroundColor': '#0176d3',
            'color': '#fff',
            '&:hover': { backgroundColor: '#0f94fe', color: '#fff' },
            '&.Mui-disabled': { color: '#fff' },
          }}
        >
          {translate('resources.units.show.links.refresh_config')}
        </Button>
        {!record?.mobile_network?.sim_id && record?.hardware_id === 3 && (
          <Button
            loading={loading && action === 'fix-missing-sim'}
            loadingPosition="start"
            size="small"
            startIcon={<Refresh />}
            onClick={fixMissingSimCard}
            disabled={loading && action === 'fix-missing-sim'}
          >
            {translate('resources.units.show.links.fix_missing_sim')}
          </Button>
        )}
        <EditTabButton />
      </TopToolbar>
      {record?.developer_options?.staging_api && (
        <Alert variant="filled" severity="warning" sx={{ mb: 2 }}>
          {translate('resources.units.uses_staging_api')}
        </Alert>
      )}
    </>
  );
};

const UnitShow = () => {
  const { hasPermission } = usePermission();
  const navigate = useNavigate();
  const currentPath = useLocation();

  useEffect(() => {
    if (/\/units\/\d+\/show/.test(currentPath.pathname) && !/units\/\d+\/show\/[a-z]+/.test(currentPath.pathname)) {
      // replace: the default-tab redirect must not leave a history entry, otherwise Back
      // pops to /units/:id/show, this effect re-fires and the user is trapped on the page.
      navigate(currentPath.pathname + '/info', { replace: true });
    }
  }, [currentPath.pathname, navigate]);

  return (
    <Show actions={<UnitActions />}>
      <TabbedShowLayout>
        <InfoTab label="resources.units.tabs.info" path="info" />
        <ConfigTab label="resources.units.tabs.configuration" path="configuration" />
        <AppsTab label="resources.units.tabs.apps" path="apps" />
        <Tab label="resources.units.tabs.commands" path="commands">
          <UnitCommandsTab />
        </Tab>
        {hasPermission('dev_options') && (
          <DevTab label="resources.units.tabs.developer_options" path="developer_options" />
        )}
        {hasPermission('dev_options') && <DiagnosticsTab label="resources.units.tabs.diagnostics" path="diagnostics" />}
        {hasPermission('dev_options') && (
          <MobileNetwork label="resources.units.tabs.mobile_network" path="mobile_network" />
        )}
        {hasPermission('dev_options') && (
          <ActivityLogTab label="resources.units.tabs.activity_log" path="activity_log" />
        )}
        {hasPermission('dev_options') && (
          <SocketsTab label="resources.units.tabs.socket_inspector" path="socket_inspector" />
        )}
        {hasPermission('report') && <ReportsTab label="resources.units.tabs.reports" path="reports" />}
      </TabbedShowLayout>
    </Show>
  );
};

export default UnitShow;
